/**
 * Reading the public API document.
 *
 * Everything this page shows is derived from the document the gateway serves at
 * `/v1/developer/openapi.json`, which is itself assembled from the services'
 * own OpenAPI documents. Nothing about an endpoint is written down here: if a
 * field is renamed in the service, the table on this page renames itself.
 */
import type { developer } from '@repletoryreach/sdk-api';

export type Doc = developer.PublicApiDocument;
export type Operation = developer.PublicApiOperation;
export type Parameter = developer.PublicApiParameter;

/** One operation, flattened with the things only its position in the document knows. */
export interface Endpoint {
  /** Stable across renders and unique: used as a React key and a DOM id. */
  id: string;
  method: string;
  path: string;
  group: string;
  scope: string;
  scopeNote?: string;
  summary: string;
  description?: string;
  deprecated: boolean;
  operation: Operation;
}

const METHOD_ORDER = ['get', 'post', 'put', 'patch', 'delete'];

/** Every operation in the document, grouped in the order the document declares its tags. */
export function readEndpoints(doc: Doc | undefined): Endpoint[] {
  if (!doc?.paths) return [];
  const tagOrder = (doc.tags ?? []).map((t) => t.name);
  const out: Endpoint[] = [];

  for (const [path, item] of Object.entries(doc.paths)) {
    for (const [method, operation] of Object.entries(item ?? {})) {
      if (!METHOD_ORDER.includes(method.toLowerCase())) continue;
      // A tag is what the document groups by. Where one is somehow absent, the
      // scope is the next most useful heading and, unlike a word like "Other",
      // it is an identifier rather than prose that would need translating.
      const group = operation.tags?.[0] ?? operation['x-rr-scope'] ?? method.toUpperCase();
      out.push({
        id: `${method.toUpperCase()} ${path}`,
        method: method.toUpperCase(),
        path,
        group,
        scope: operation['x-rr-scope'] ?? '',
        scopeNote: operation['x-rr-scope-note'],
        summary: operation.summary ?? `${method.toUpperCase()} ${path}`,
        description: operation.description,
        deprecated: operation.deprecated === true,
        operation
      });
    }
  }

  return out.sort((a, b) => {
    const ga = tagOrder.indexOf(a.group);
    const gb = tagOrder.indexOf(b.group);
    if (ga !== gb) return (ga < 0 ? 99 : ga) - (gb < 0 ? 99 : gb);
    if (a.path !== b.path) return a.path.localeCompare(b.path);
    return METHOD_ORDER.indexOf(a.method.toLowerCase()) - METHOD_ORDER.indexOf(b.method.toLowerCase());
  });
}

/** The distinct groups, in document order. */
export function readGroups(endpoints: Endpoint[]): string[] {
  const seen: string[] = [];
  for (const e of endpoints) if (!seen.includes(e.group)) seen.push(e.group);
  return seen;
}

/**
 * A write is anything that is not a GET. The page marks these before it will
 * run one, because "Try it" is a real call against the developer's real account
 * and there is no sandbox to hide behind.
 */
export function isWrite(method: string): boolean {
  return method.toUpperCase() !== 'GET';
}

/** A DELETE cannot be undone, so the page asks for the resource to be named first. */
export function isDestructive(method: string): boolean {
  return method.toUpperCase() === 'DELETE';
}

// ── schemas ────────────────────────────────────────────────────────────────

export interface SchemaObject {
  type?: string | string[];
  format?: string;
  description?: string;
  properties?: Record<string, SchemaObject>;
  required?: string[];
  items?: SchemaObject;
  enum?: unknown[];
  $ref?: string;
  allOf?: SchemaObject[];
  oneOf?: SchemaObject[];
  anyOf?: SchemaObject[];
  [key: string]: unknown;
}

/** Follows a `$ref` into the document's own components. */
export function resolve(schema: SchemaObject | undefined, doc: Doc | undefined): SchemaObject | undefined {
  if (!schema) return undefined;
  if (typeof schema.$ref === 'string' && schema.$ref.startsWith('#/components/schemas/')) {
    const name = schema.$ref.slice('#/components/schemas/'.length);
    const target = doc?.components?.schemas?.[name] as SchemaObject | undefined;
    // A ref that does not resolve returns the ref itself rather than nothing, so
    // the table shows a row naming the missing schema instead of silently
    // dropping the field.
    return target ? resolve(target, doc) : schema;
  }
  return schema;
}

/** The schema name behind a `$ref`, for showing what a response is called. */
export function refName(schema: SchemaObject | undefined): string | null {
  if (typeof schema?.$ref !== 'string') return null;
  const parts = schema.$ref.split('/');
  return parts[parts.length - 1] ?? null;
}

export interface Field {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  /** Nesting level, so the table can indent rather than flatten everything flat. */
  depth: number;
}

/**
 * Flattens a schema into rows a table can render, following `$ref` and
 * descending into nested objects up to `maxDepth`. Deeper than that the row
 * names the type and stops: a fully expanded tree of every envelope is a wall,
 * not a reference.
 */
export function readFields(
  schema: SchemaObject | undefined,
  doc: Doc | undefined,
  depth = 0,
  maxDepth = 2
): Field[] {
  const resolved = resolve(schema, doc);
  if (!resolved) return [];

  // A nullable field is published as a two-branch oneOf; the real shape is the
  // branch that is not null.
  const branches = resolved.oneOf ?? resolved.anyOf ?? resolved.allOf;
  if (branches && !resolved.properties) {
    const real = branches.find((b) => resolve(b, doc)?.type !== 'null' && b.type !== 'null');
    if (real) return readFields(real, doc, depth, maxDepth);
  }

  if (resolved.type === 'array' || (Array.isArray(resolved.type) && resolved.type.includes('array'))) {
    return readFields(resolved.items, doc, depth, maxDepth);
  }

  const properties = resolved.properties;
  if (!properties) return [];
  const required = new Set(resolved.required ?? []);

  const rows: Field[] = [];
  for (const [name, raw] of Object.entries(properties)) {
    const field = resolve(raw, doc) ?? raw;
    rows.push({
      name,
      type: describeType(raw, doc),
      required: required.has(name),
      description: raw.description ?? field.description,
      depth
    });
    if (depth < maxDepth) {
      const nested = readFields(raw, doc, depth + 1, maxDepth);
      // Only descend where there is genuinely more structure to show.
      if (nested.length > 0 && nested.length <= 20) rows.push(...nested);
    }
  }
  return rows;
}

/** A short, honest name for a field's type, including whether it may be null. */
export function describeType(schema: SchemaObject | undefined, doc: Doc | undefined): string {
  if (!schema) return 'unknown';

  const named = refName(schema);
  const resolved = resolve(schema, doc);
  if (!resolved) return named ?? 'unknown';

  const branches = resolved.oneOf ?? resolved.anyOf;
  if (branches) {
    const nullable = branches.some((b) => b.type === 'null');
    const real = branches.find((b) => b.type !== 'null');
    const inner = real ? describeType(real, doc) : 'unknown';
    return nullable ? `${inner} or null` : inner;
  }

  const types = Array.isArray(resolved.type) ? resolved.type : resolved.type ? [resolved.type] : [];
  const nullable = types.includes('null');
  const primary = types.find((t) => t !== 'null');

  let base: string;
  if (primary === 'array') {
    const item = refName(resolved.items as SchemaObject) ?? describeType(resolved.items, doc);
    base = `${item}[]`;
  } else if (primary === 'object' || (!primary && resolved.properties)) {
    base = named ?? 'object';
  } else if (resolved.format) {
    base = resolved.format;
  } else {
    base = primary ?? named ?? 'unknown';
  }

  return nullable ? `${base} or null` : base;
}

/** The JSON example the document carries for a request body, if any. */
export function requestExample(operation: Operation): unknown {
  return operation.requestBody?.content?.['application/json']?.example;
}

/** The request body's schema, if the operation takes one. */
export function requestSchema(operation: Operation): SchemaObject | undefined {
  return operation.requestBody?.content?.['application/json']?.schema as SchemaObject | undefined;
}

/** Responses in status order, so 2xx reads before 4xx. */
export function readResponses(operation: Operation): { status: string; description: string; schema?: SchemaObject }[] {
  return Object.entries(operation.responses ?? {})
    .map(([status, value]) => ({
      status,
      description: value?.description ?? '',
      schema: value?.content?.['application/json']?.schema as SchemaObject | undefined
    }))
    .sort((a, b) => a.status.localeCompare(b.status));
}

export function pathParameters(operation: Operation): Parameter[] {
  return (operation.parameters ?? []).filter((p) => p.in === 'path');
}

export function queryParameters(operation: Operation): Parameter[] {
  return (operation.parameters ?? []).filter((p) => p.in === 'query');
}

export function headerParameters(operation: Operation): Parameter[] {
  return (operation.parameters ?? []).filter((p) => p.in === 'header');
}
